// ============================================================
// SideQuests v7 - Auth Context (Google + Phone + Email + Facebook Connect)
//
// PERFORMANCE (first-visit JS pass): this module is imported by the app shell
// (App.tsx), so it used to force firebase/auth + firebase/firestore into the
// FIRST-LOAD graph of every route, including pure static pages like /about.
// It now imports only TYPES from those SDKs and resolves the real instances
// lazily via authApi()/firestoreApi() inside the effects and handlers that
// genuinely need them. Behaviour is unchanged: same providers, same
// GoogleAuthProvider config, same user-doc bootstrap, same sign-out.
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { User, ConfirmationResult } from 'firebase/auth';
import { authApi, firestoreApi } from '../firebase';
import { UserRole, AppUser } from '../types';
import { toast } from '../services/toast';
import { initFCM, enableNotifications as enableNotificationsForUser } from '../services/notifications';

// ⭐ Previously a module-scope `new GoogleAuthProvider()` which pulled the
// whole firebase/auth SDK into the entry chunk. The provider is now created
// on demand inside signInWithGoogle() with the identical configuration.
function createGoogleProvider(GoogleAuthProvider: any) {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

interface AuthContextType {
  user: User | null;
  appUser: AppUser | null;
  loading: boolean;
  role: UserRole;
  setRole: (role: UserRole) => void;
  signInWithGoogle: () => Promise<void>;
  signInWithPhone: (phoneNumber: string, verifier: any) => Promise<ConfirmationResult>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (data: Partial<AppUser>) => Promise<void>;
  refreshAppUser: () => Promise<void>;
  connectFacebook: (fbProfileUrl: string) => Promise<void>;
  facebookConnected: boolean;
  idPending: boolean;
  /** Explicit, gesture-driven notification enablement (contextual UX). */
  enableNotifications: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function genReferralCode(name: string): string {
  const base = name.replace(/\s/g, '').toLowerCase();
  const suffix = Math.random().toString(36).substring(2, 7).toUpperCase();
  return `${base}-${suffix}`;
}

/** Firebase Auth persists sessions under this localStorage key prefix. */
const AUTH_PERSIST_PREFIX = 'firebase:authUser:';

/**
 * Is there a persisted Firebase Auth session in this browser?
 *
 * If there is not, the user is definitively signed out: observing auth state
 * could only ever yield `null`. In that case we skip downloading firebase/auth
 * entirely on first paint, which is what keeps the SDK out of the cold-load
 * graph of every public route.
 */
function hasPersistedAuthSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(AUTH_PERSIST_PREFIX)) return true;
    }
  } catch {
    /* storage blocked (private mode / SSR) - fall through to "no session" */
  }
  return false;
}

async function createUserDoc(firebaseUser: User, extra: Record<string, any> = {}) {
  const { db, doc, getDoc, setDoc, serverTimestamp } = await firestoreApi();
  const userRef = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    const refCode = genReferralCode(firebaseUser.displayName || firebaseUser.email || 'User');
    await setDoc(userRef, {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      phone: firebaseUser.phoneNumber || '',
      displayName: firebaseUser.displayName || extra.displayName || firebaseUser.email?.split('@')[0] || 'User',
      photoURL: firebaseUser.photoURL || '',
      role: null,
      referralCode: refCode,
      referralCount: 0,
      createdAt: serverTimestamp(),
      onboarded: false,
      agreedToTos: true,
      authProvider: extra.authProvider || 'email',
      ...extra,
    });
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRoleState] = useState<UserRole>(null);

  const facebookConnected = !!(appUser?.fbId || appUser?.fbProfileUrl);
  const idPending = appUser?.idVerifiedStatus === 'pending';

  // ⭐ Track F (Best Practices): location consent moved fully behind user
  // action. This flag can be set true only by explicit in-app flows
  // (onboarding "Use my location", PostPage "pin my location", etc.).
  const requestLocationAccess = useCallback(async (_firebaseUser: User) => {
    // Geolocation is intentionally NEVER requested here on page load.
    // Requesting on every returning visit caused a permission prompt on
    // first paint; Lighthouse flags it as "avoid requesting permission on
    // page load". Onboarding/PostPage/HomePage request location from an
    // explicit button press instead. We only persist the granted flag if a
    // position was already captured elsewhere this session.
    const granted = typeof navigator !== 'undefined' && navigator.geolocation !== undefined;
    return granted;
  }, []);

  // ⭐ NOTIFICATION UX (2026-08-21): NEVER request permission on sign-in or
  // page load. initFCM is now silent - it only registers the SW and re-saves
  // the token when permission is already granted. Contextual prompts are
  // triggered explicitly by user actions (posting, claiming, visiting
  // Messages) via `enableNotifications`, which calls the browser-native
  // permission API from a user gesture.
  const enableNotifications = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const result = await enableNotificationsForUser(user.uid);
    return result === 'granted';
  }, [user]);

  /** Build a minimal AppUser from the Firebase Auth user alone - used as
   *  fallback when the Firestore user doc can't be read (network error,
   *  App Check throttle, permission denied, etc.) so the profile page
   *  can still render instead of staying stuck behind a loading spinner. */
  function buildFallbackAppUser(firebaseUser: User): AppUser {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || undefined,
      phone: firebaseUser.phoneNumber || undefined,
      displayName: firebaseUser.displayName || undefined,
      photoURL: firebaseUser.photoURL || undefined,
      role: null,
      createdAt: new Date(),
      onboarded: false,
      badges: [],
    };
  }

  async function fetchAppUser(firebaseUser: User) {
    try {
      const { db, doc, getDoc } = await firestoreApi();
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const appUserData: AppUser = {
          uid: firebaseUser.uid,
          email: data.email || firebaseUser.email || undefined,
          phone: data.phone || firebaseUser.phoneNumber || undefined,
          displayName: data.displayName || firebaseUser.displayName || undefined,
          photoURL: data.photoURL || firebaseUser.photoURL || undefined,
          role: data.role || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          onboarded: data.onboarded || false,
          fbId: data.fbId || undefined,
          fbProfileUrl: data.fbProfileUrl || undefined,
          fbName: data.fbName || undefined,
          showFbProfile: data.showFbProfile ?? true,
          fbVerified: data.fbVerified ?? false,
          idVerifiedStatus: data.idVerifiedStatus || 'none',
          idDocumentType: data.idDocumentType || undefined,
          idCountry: data.idCountry || undefined,
          selfieVerifiedStatus: data.selfieVerifiedStatus || 'none',
          referredBy: data.referredBy || undefined,
          referralCode: data.referralCode || undefined,
          referralCount: data.referralCount || 0,
          reliabilityScore: data.reliabilityScore || 0,
          responseRate: data.responseRate || 0,
          completedGigsCount: data.completedGigsCount || 0,
          avgPaymentAmount: data.avgPaymentAmount || 0,
          totalEarnings: data.totalEarnings || 0,
          totalTips: data.totalTips || 0,
          avgRating: data.avgRating || 0,
          stripeConnectId: data.stripeConnectId || undefined,
          stripeOnboardingComplete: data.stripeOnboardingComplete || false,
          badges: data.badges || [],
        };
        setAppUser(appUserData);
        if (data.role) setRoleState(data.role);
      } else {
        setAppUser(buildFallbackAppUser(firebaseUser));
      }
    } catch (e) {
      console.error('Error fetching user data:', e);
      // CRITICAL: always set appUser so the profile page can render.
      // Without this, a Firestore error leaves appUser=null and the
      // profileGate stays in 'loading' forever (infinite spinner).
      setAppUser(buildFallbackAppUser(firebaseUser));
    }
  }

  // ⭐ FIRST-VISIT JS PASS: firebase/auth is an async chunk, but starting the
  // auth observer unconditionally still pulled it into the cold-load graph of
  // EVERY route - including static pages like /about that never use it.
  //
  // Observation is now started on demand:
  //   * a visitor with a persisted session is a returning signed-in user, so
  //     observation starts immediately and signed-in refresh / private-route
  //     gating / sign-out behave exactly as before;
  //   * a visitor with NO persisted session is definitively signed out, so
  //     the SDK is never downloaded and the signed-out UI renders at once;
  //   * signing in (any provider) starts observation via startAuthObserver();
  //   * another tab signing in is picked up by the `storage` listener.
  const observerStartedRef = useRef(false);
  const observerUnsubRef = useRef<(() => void) | null>(null);

  const startAuthObserver = useCallback(() => {
    if (observerStartedRef.current) return;
    observerStartedRef.current = true;

    (async () => {
      try {
        const { auth, onAuthStateChanged } = await authApi();
        if (!observerStartedRef.current) return;
        observerUnsubRef.current = onAuthStateChanged(auth, async (firebaseUser) => {
          setUser(firebaseUser);
          if (firebaseUser) {
            await fetchAppUser(firebaseUser);
            await requestLocationAccess(firebaseUser);
            // FCM: silent bootstrap only - never prompts, never opens a modal.
            initFCM(firebaseUser.uid);
          } else {
            setAppUser(null);
            setRoleState(null);
          }
          setLoading(false);
        });
      } catch (e) {
        console.error('Auth state observation failed to start:', e);
        setLoading(false);
      }
    })();
  }, [requestLocationAccess]);

  useEffect(() => {
    let watchdog: ReturnType<typeof setTimeout> | null = null;

    if (hasPersistedAuthSession()) {
      startAuthObserver();
      // Watchdog: never let the app sit on the loading state forever.
      // If auth state hasn't resolved within 12s (network stall, App Check
      // throttle, offline), release the UI so pages can render their own
      // empty/retry states instead of a permanent spinner/blank screen.
      watchdog = setTimeout(() => setLoading(false), 12000);
    } else {
      // Definitively signed out - no SDK, no spinner, no waiting.
      setLoading(false);
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.startsWith(AUTH_PERSIST_PREFIX)) startAuthObserver();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      if (watchdog) clearTimeout(watchdog);
      window.removeEventListener('storage', onStorage);
      observerUnsubRef.current?.();
      observerUnsubRef.current = null;
      observerStartedRef.current = false;
    };
  }, [requestLocationAccess, startAuthObserver]);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      // Start observation BEFORE signing in so onAuthStateChanged publishes
      // the new user to the app (the observer is not running by default on a
      // first, signed-out visit).
      startAuthObserver();
      const { auth, signInWithPopup, GoogleAuthProvider } = await authApi();
      const result = await signInWithPopup(auth, createGoogleProvider(GoogleAuthProvider));
      await createUserDoc(result.user, { authProvider: 'google' });
      await fetchAppUser(result.user);
      toast('Signed in with Google!');
    } catch (e: any) {
      if (e?.code === 'auth/popup-closed-by-user') {
        toast('Sign-in cancelled.', 'info');
      } else {
        toast(e?.message || 'Google sign-in failed.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithPhone = useCallback(async (phoneNumber: string, verifier: any) => {
    setLoading(true);
    try {
      startAuthObserver();
      const { auth, signInWithPhoneNumber } = await authApi();
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      toast('Verification code sent!');
      return confirmation;
    } catch (e: any) {
      toast(e?.message || 'Phone sign-in failed.', 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, displayName: string) => {
    setLoading(true);
    try {
      startAuthObserver();
      const { auth, createUserWithEmailAndPassword, sendEmailVerification } = await authApi();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await createUserDoc(result.user, { displayName, authProvider: 'email' });
      await sendEmailVerification(result.user);
      await fetchAppUser(result.user);
      toast('Account created! Check your email to verify.');
    } catch (e: any) {
      toast(e?.message || 'Sign up failed.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithEmailFn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      startAuthObserver();
      const { auth, signInWithEmailAndPassword } = await authApi();
      const result = await signInWithEmailAndPassword(auth, email, password);
      await createUserDoc(result.user, { authProvider: 'email' });
      await fetchAppUser(result.user);
      toast('Signed in!');
    } catch (e: any) {
      toast(e?.message || 'Sign-in failed.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    const { auth, signOut: firebaseSignOut } = await authApi();
    await firebaseSignOut(auth);
    setRoleState(null);
    toast('Signed out.');
  }, []);

  const updateUserProfile = useCallback(async (data: Partial<AppUser>) => {
    if (!user) return;
    const { db, doc, updateDoc, serverTimestamp } = await firestoreApi();
    await updateDoc(doc(db, 'users', user.uid), { ...data, updatedAt: serverTimestamp() });
    // Keep Firebase Auth's photo field in sync when the avatar changes, so any
    // component reading `user.photoURL` shows the same image immediately.
    if (data.photoURL) {
      try {
        const { updateProfile } = await authApi();
        await updateProfile(user, { photoURL: data.photoURL });
      } catch { /* best-effort */ }
    }
    setAppUser(prev => prev ? { ...prev, ...data } : prev);
    toast('Profile updated!');
  }, [user]);

  const refreshAppUser = useCallback(async () => {
    if (user) await fetchAppUser(user);
  }, [user]);

  // ⭐ HARDENING (2026-08-21): connectFacebook now takes a Facebook profile
  // URL (never a name + arbitrary photo). The URL is validated client-side
  // before it reaches this point, and it is stored as a profile link -
  // NOT a verification signal. fbVerified always stays false; the trust
  // engines on both client and server explicitly ignore facebook links.
  const connectFacebook = useCallback(async (fbProfileUrl: string) => {
    if (!user) return;
    const { db, doc, updateDoc, serverTimestamp, arrayUnion } = await firestoreApi();
    const segments = fbProfileUrl.split('/').filter(Boolean);
    const fbName = decodeURIComponent(segments[segments.length - 1]?.split('?')[0] || 'Facebook');
    await updateDoc(doc(db, 'users', user.uid), {
      fbId: user.uid, fbProfileUrl, showFbProfile: true,
      fbName, fbVerified: false,
      badges: arrayUnion('fb_connected'), updatedAt: serverTimestamp(),
    });
    setAppUser(prev => prev ? {
      ...prev, fbId: user.uid, fbProfileUrl, fbName, fbVerified: false,
      badges: [...(prev.badges || []), 'fb_connected'],
    } : prev);
    toast('Facebook profile link saved!', 'success');
  }, [user]);

  const setRole = useCallback((newRole: UserRole) => { setRoleState(newRole); }, []);

  return (
    <AuthContext.Provider value={{
      user, appUser, loading, role, setRole,
      signInWithGoogle, signInWithPhone,
      signUpWithEmail, signInWithEmail: signInWithEmailFn,
      signOut, updateUserProfile, refreshAppUser,
      connectFacebook, facebookConnected, idPending,
      enableNotifications,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
